/**
 * Parabole — défi « Qui est f ? » (pur). Une parabole tracée sur le
 * quadrillage ; quatre expressions dont une seule est la sienne. Les erreurs
 * proposées sont les classiques : (x + α) au lieu de (x − α), β de l'autre
 * signe, a du mauvais signe (ouverture), racines de signes contraires.
 */

import { frac, value, type Frac } from '../../ui/exact';
import {
  F,
  beta,
  canonicalText,
  developedText,
  factoredText,
  fromCanonical,
  fromRoots,
  type Tri,
} from './trinomial';

export type QuizMistake = 'alpha' | 'beta' | 'a' | 'roots' | 'scale' | 'scaleRoots';

export interface QuizOption {
  text: string;
  tri: Tri;
  correct: boolean;
  mistake: QuizMistake | null;
}

export interface Quiz {
  tri: Tri;
  form: 'canonical' | 'factored';
  options: QuizOption[];
}

export const QUIZ_HINT: Record<QuizMistake, string> = {
  alpha:
    'Le sommet a pour abscisse α et la forme canonique s’écrit a(x − α)² + β : si α = 2, on écrit (x − 2)², pas (x + 2)².',
  beta: 'β est l’ordonnée du sommet : lis-la sur l’axe vertical, avec son signe.',
  a: 'Regarde l’ouverture : tournée vers le haut, a > 0 ; vers le bas, a < 0.',
  roots:
    'Les racines se lisent là où la courbe coupe l’axe des abscisses ; a(x − x₁)(x − x₂) s’annule en x₁ et en x₂.',
  scale:
    'Le signe est bon mais pas l’écartement : depuis le sommet, un pas de 1 vers la droite fait monter (ou descendre) de a.',
  scaleRoots:
    'Les racines sont bonnes mais pas a : lis l’ordonnée à l’origine, f(0) = a × (0 − x₁) × (0 − x₂), pour trouver a.',
};

const pick = <T>(list: readonly T[], rnd: () => number): T =>
  list[Math.floor(rnd() * list.length)]!;

function shuffle<T>(list: readonly T[], rnd: () => number): T[] {
  const a = [...list];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rnd() * (i + 1));
    [a[i], a[j]] = [a[j]!, a[i]!];
  }
  return a;
}

const neg = (f: Frac) => frac(-f.p, f.q);

export function makeQuiz(rnd: () => number): Quiz {
  const form = rnd() < 0.5 ? 'canonical' : 'factored';
  const a = F(pick([-2, -1, -0.5, 0.5, 1, 2], rnd));
  let tri: Tri;
  const wrong: Array<{ tri: Tri; mistake: QuizMistake }> = [];
  if (form === 'canonical') {
    const al = F(pick([-3, -2, -1, 1, 2, 3], rnd));
    // le point (α ± 1 ; β + a), qui donne a, reste dans la figure
    const bes = [-4, -3, -2, -1, 1, 2, 3, 4].filter((b) => Math.abs(b + a.p / a.q) <= 5);
    const be = F(pick(bes, rnd));
    tri = fromCanonical(a, al, be);
    wrong.push({ tri: fromCanonical(a, neg(al), be), mistake: 'alpha' });
    wrong.push({ tri: fromCanonical(a, al, neg(be)), mistake: 'beta' });
    wrong.push({ tri: fromCanonical(neg(a), al, be), mistake: 'a' });
    wrong.push({ tri: fromCanonical(F((a.p / a.q) * 2), al, be), mistake: 'scale' });
  } else {
    // sommet et ordonnée à l'origine lisibles dans la figure (|y| ≤ 5)
    const pairs: Array<[number, number]> = [];
    for (const u of [-4, -3, -2, -1, 0])
      for (const v of [1, 2, 3, 4]) {
        const t = fromRoots(a, F(u), F(v));
        if (Math.abs(value(beta(t))) <= 5 && Math.abs(value(t.c)) <= 5) pairs.push([u, v]);
      }
    const [x1, x2] = pick(pairs, rnd);
    tri = fromRoots(a, F(x1), F(x2));
    wrong.push({ tri: fromRoots(a, F(-x1), F(-x2)), mistake: 'roots' });
    wrong.push({ tri: fromRoots(neg(a), F(x1), F(x2)), mistake: 'a' });
    wrong.push({ tri: fromRoots(F((a.p / a.q) * 2), F(x1), F(x2)), mistake: 'scaleRoots' });
    wrong.push({ tri: fromRoots(a, F(-x1), F(x2)), mistake: 'roots' });
  }
  const write = (t: Tri) => (form === 'canonical' ? canonicalText(t) : (factoredText(t) ?? ''));
  const seen = new Set([developedText(tri)]);
  const options: QuizOption[] = [{ text: write(tri), tri, correct: true, mistake: null }];
  for (const w of shuffle(wrong, rnd)) {
    const key = developedText(w.tri);
    if (seen.has(key) || options.length >= 4) continue;
    seen.add(key);
    options.push({ text: write(w.tri), tri: w.tri, correct: false, mistake: w.mistake });
  }
  return { tri, form, options: shuffle(options, rnd) };
}
